import { useEffect, useRef, useState } from 'react';
import { IconPackage, IconRocket } from '../agent/components/icons';
import type { SaveState } from '../persistence/saveState';
import type { SandboxTemplate } from '../templates';

export interface SandboxHeaderProps {
  templates: readonly Pick<SandboxTemplate, 'name' | 'description'>[];
  /** The template the editor was last loaded from: what the template selector shows. */
  currentTemplateIndex: number;
  onTemplateChange: (index: number) => void;
  onCreateNewSandbox: (templateIndex: number) => void;
  onRun: () => void;
  /** The sandbox backend answered its health check, so Save and Fork are offered. */
  backendAvailable: boolean;
  isSaving: boolean;
  /** The saved sandbox the editor shows, if any: Save's tooltip names it. */
  currentSandbox: { hash: string; revision: number } | null;
  onSave: () => void;
  onFork: () => void;
  onImport: () => void;
  onExportGbox: () => void;
  onExportStandalone: () => void;
  /** Reset is offered while the editor holds a template. */
  showReset: boolean;
  onReset: () => void;
  agentOpen: boolean;
  onToggleAgent: () => void;
  /** Where the project stands against what was saved (`saveStateOf`): shown beside Save, with or without a backend. */
  saveState: { state: SaveState; text: string };
}

/**
 * The sandbox's toolbar: new sandbox, template, run, save, fork, import, export, reset and the agent. It owns its three
 * dropdowns (open, closed by an item or a click outside them); every action is a callback, so the app keeps the logic.
 */
export function SandboxHeader({
  templates, currentTemplateIndex, onTemplateChange, onCreateNewSandbox, onRun, backendAvailable, isSaving,
  currentSandbox, onSave, onFork, onImport, onExportGbox, onExportStandalone, showReset, onReset, agentOpen,
  onToggleAgent, saveState,
}: SandboxHeaderProps) {
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [newSandboxDropdownOpen, setNewSandboxDropdownOpen] = useState(false);
  const [selectedNewTemplateIndex, setSelectedNewTemplateIndex] = useState(0);
  const exportContainerRef = useRef<HTMLDivElement>(null);
  const templateContainerRef = useRef<HTMLDivElement>(null);
  const newSandboxContainerRef = useRef<HTMLDivElement>(null);

  // A click outside a dropdown closes it.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (exportContainerRef.current && !exportContainerRef.current.contains(target)) setExportDropdownOpen(false);
      if (templateContainerRef.current && !templateContainerRef.current.contains(target)) setTemplateDropdownOpen(false);
      if (newSandboxContainerRef.current && !newSandboxContainerRef.current.contains(target)) setNewSandboxDropdownOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <header className="header">
      <div className="logo-container">
        <div className="logo-wrapper">
          <img src="/gotti-logo.png" alt="Gotti" className="logo" />
        </div>
        <span className="sandbox-badge">sandbox</span>
      </div>

      {/* New Sandbox Button */}
      <div className="new-sandbox-dropdown-container" ref={newSandboxContainerRef}>
        <button
          className="new-sandbox-btn"
          data-testid="header-new-sandbox"
          onClick={(e) => {
            e.stopPropagation();
            setNewSandboxDropdownOpen(!newSandboxDropdownOpen);
          }}
          title="Create a new sandbox"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>New Sandbox</span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        {newSandboxDropdownOpen && (
          <div className="new-sandbox-dropdown">
            <div className="new-sandbox-dropdown-header">Choose a template:</div>
            <div className="new-sandbox-template-list">
              {templates.map((template, index) => (
                <label
                  key={template.name}
                  className={`new-sandbox-template-option ${index === selectedNewTemplateIndex ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="new-sandbox-template"
                    checked={index === selectedNewTemplateIndex}
                    onChange={() => setSelectedNewTemplateIndex(index)}
                  />
                  <span className="template-option-content">
                    <span className="template-option-name">{template.name}</span>
                    <span className="template-option-desc">{template.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="new-sandbox-dropdown-footer">
              <button
                className="new-sandbox-create-btn"
                onClick={() => {
                  setNewSandboxDropdownOpen(false);
                  onCreateNewSandbox(selectedNewTemplateIndex);
                }}
              >
                Create
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Template Selector */}
      <div className="template-dropdown-container" ref={templateContainerRef}>
        <button
          className="template-selector-btn"
          onClick={(e) => {
            e.stopPropagation();
            setTemplateDropdownOpen(!templateDropdownOpen);
          }}
          title="Select a template"
        >
          <span className="template-name">{templates[currentTemplateIndex]?.name || 'Select Template'}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        {templateDropdownOpen && (
          <div className="template-dropdown">
            {templates.map((template, index) => (
              <button
                key={template.name}
                className={`template-dropdown-item ${index === currentTemplateIndex ? 'active' : ''}`}
                onClick={() => {
                  setTemplateDropdownOpen(false);
                  onTemplateChange(index);
                }}
              >
                <span className="template-item-name">{template.name}</span>
                <span className="template-item-desc">{template.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <ul className="toolbar-actions">
        {/* Run */}
        <li className="toolbar-item">
          <button
            id="run-btn"
            data-testid="header-run"
            className="toolbar-btn"
            onClick={onRun}
            title="Run sandbox (Cmd+Enter)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <g strokeLinejoin="round" strokeLinecap="round" strokeWidth="2.2" fill="none" stroke="currentColor">
                <path d="M23 4v6h-6"></path>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </g>
            </svg>
            <span>Run</span>
          </button>
        </li>

        {/* Save state */}
        <li className="toolbar-item">
          <span
            className={`save-state save-state-${saveState.state}`}
            data-testid="save-state"
            data-state={saveState.state}
            role="status"
            aria-live="polite"
            title={saveState.text}
          >
            {saveState.text}
          </span>
        </li>

        {/* Save */}
        {backendAvailable && (
          <li className="toolbar-item">
            <button
              id="save-btn"
              data-testid="header-save"
              className="toolbar-btn"
              onClick={onSave}
              disabled={isSaving}
              title={currentSandbox
                ? `Save new revision (Cmd+S) - Current: /${currentSandbox.hash}/${currentSandbox.revision}`
                : 'Save to cloud (Cmd+S)'
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16"></polyline>
                <line x1="12" y1="12" x2="12" y2="21"></line>
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
                <polyline points="16 16 12 12 8 16"></polyline>
              </svg>
            </button>
          </li>
        )}

        {/* Fork */}
        {backendAvailable && (
          <li className="toolbar-item">
            <button
              id="fork-btn"
              data-testid="header-fork"
              className="toolbar-btn"
              onClick={onFork}
              disabled={isSaving}
              title="Fork into a new sandbox"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 8C7.10457 8 8 7.10457 8 6C8 4.89543 7.10457 4 6 4C4.89543 4 4 4.89543 4 6C4 7.10457 4.89543 8 6 8Z"></path>
                <path d="M12 20C13.1046 20 14 19.1046 14 18C14 16.8954 13.1046 16 12 16C10.8954 16 10 16.8954 10 18C10 19.1046 10.8954 20 12 20Z"></path>
                <path d="M18 8C19.1046 8 20 7.10457 20 6C20 4.89543 19.1046 4 18 4C16.8954 4 16 4.89543 16 6C16 7.10457 16.8954 8 18 8Z"></path>
                <path d="M6.01734 8.74104C6.01734 10.4146 5.77537 12.1999 9.22051 11.9858H12.0053M17.9929 8.57654C18.1259 11.9858 16.9199 11.7651 15.7861 11.9858H12.0053M12.0053 15.7005V11.9858"></path>
              </svg>
            </button>
          </li>
        )}

        <li className="toolbar-divider"></li>

        {/* Import */}
        <li className="toolbar-item">
          <button
            id="import-btn"
            data-testid="header-import"
            className="toolbar-btn"
            onClick={onImport}
            title="Import .gbox file"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </button>
        </li>

        {/* Export/Download */}
        <li className="toolbar-item">
          <div className="export-dropdown-container" ref={exportContainerRef}>
            <button
              id="export-btn"
              data-testid="header-export"
              className="toolbar-btn"
              onClick={(e) => {
                e.stopPropagation();
                setExportDropdownOpen(!exportDropdownOpen);
              }}
              title="Download/Export"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </button>
            {exportDropdownOpen && (
              <div className="export-dropdown">
                <button
                  className="export-dropdown-item"
                  data-testid="header-export-gbox"
                  onClick={() => {
                    setExportDropdownOpen(false);
                    onExportGbox();
                  }}
                >
                  <span className="export-icon"><IconPackage /></span>
                  <span className="export-label">
                    <strong>As Sandbox (.gbox)</strong>
                    <small>Re-import into Sandbox later</small>
                  </span>
                </button>
                <button
                  className="export-dropdown-item"
                  data-testid="header-export-standalone"
                  onClick={() => {
                    setExportDropdownOpen(false);
                    onExportStandalone();
                  }}
                >
                  <span className="export-icon"><IconRocket /></span>
                  <span className="export-label">
                    <strong>As Standalone Project</strong>
                    <small>ZIP with package.json for local dev</small>
                  </span>
                </button>
              </div>
            )}
          </div>
        </li>

        <li className="toolbar-divider"></li>

        {/* Reset - only shown when working from a template */}
        {showReset && (
        <li className="toolbar-item">
          <button
            id="reset-btn"
            data-testid="header-reset"
            className="toolbar-btn toolbar-btn-danger"
            onClick={onReset}
            title="Reset to template defaults"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10"></polyline>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
            </svg>
          </button>
        </li>
        )}

        <li className="toolbar-divider"></li>

        {/* Agent Toggle */}
        <li className="toolbar-item">
          <button
            id="agent-btn"
            data-testid="header-agent"
            className={`toolbar-btn ${agentOpen ? 'active' : ''}`}
            onClick={onToggleAgent}
            title={agentOpen ? 'Close Agent Chat' : 'Open Agent Chat'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              <circle cx="9" cy="10" r="1" fill="currentColor"></circle>
              <circle cx="12" cy="10" r="1" fill="currentColor"></circle>
              <circle cx="15" cy="10" r="1" fill="currentColor"></circle>
            </svg>
            <span>Agent</span>
          </button>
        </li>
      </ul>
    </header>
  );
}
